// Copyright (c) 2025 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { catchError, filter, groupBy, mergeMap, switchMap } from "rxjs/operators"
import { create } from "@bufbuild/protobuf"
import { showSnackBar } from "../../snackbar/actions"
import * as Actions from "../actions"
import * as Slice from "../slice"
import * as TimelineActions from "../../timeline/actions"
import { Code, executorMutation, Init, isConnectError, timesheetFieldService } from "../../../app/rpc"
import {
  ListTimesheetFieldOptionsRequestSchema,
  ListTimesheetFieldsRequestSchema,
  TimesheetFieldOption,
  UpdateTimesheetFieldOptionRequestSchema,
} from "../../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"

type UpdateTimesheetFieldOptionRpc = (request: Init<typeof UpdateTimesheetFieldOptionRequestSchema>) => Observable<TimesheetFieldOption>

export const updateTimesheetFieldOption = (updateTimesheetFieldOptionRpc: UpdateTimesheetFieldOptionRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.updateTimesheetFieldOption.match),
      groupBy(action => action.payload.request.id ?? ""),
      mergeMap(group$ =>
        group$.pipe(
          switchMap(action =>
            updateTimesheetFieldOptionRpc(action.payload.request).pipe(
              mergeMap((response) => of(Slice.updateTimesheetFieldOption({
                option: response,
                timesheetFieldId: action.payload.timesheetFieldId,
              }))),
              catchError(e => handleError(e, action.payload)),
            )
          )
        )
      ),
    )

const handleError = (e: unknown, payload: Actions.UpdateTimesheetFieldOptionPayload): Observable<Action> => {
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.NotFound) {
      return concat([
        TimelineActions.listTimesheetFields({
          request: create(ListTimesheetFieldsRequestSchema, { siteId: payload.request.siteId }),
          includeOptions: true,
        }),
        showSnackBar({
          message: t({
            id: "msg_timesheet_field_or_option_not_found",
            message: "Timesheet field or option not found.",
          }),
          messageType: "ERROR",
        }),
      ])
    }
    if (code === Code.FailedPrecondition) {
      return concat([
        Actions.listTimesheetFieldOptions(
          create(ListTimesheetFieldOptionsRequestSchema, {
            siteId: payload.request.siteId,
            timesheetFieldId: payload.timesheetFieldId,
          })
        ),
        showSnackBar({
          message: t({
            id: "msg_update_timesheet_field_option_failed_precondition",
            message: "Failed to update timesheet field option. Duplicate label or value.",
          }),
          messageType: "ERROR",
        }),
      ])
    }
    if (code === Code.PermissionDenied) {
      return of(showSnackBar({
        message: t({
          id: "msg_update_timesheet_field_option_permission_denied",
          message: "Failed to update timesheet field option. You do not have permission.",
        }),
        messageType: "ERROR",
      }))
    }
  }
  return of(showSnackBar({
    message: t({
      id: "msg_update_timesheet_field_option_failed",
      message: "Failed to update timesheet field option. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
    }),
    messageType: "ERROR",
  }))
}

const updateTimesheetFieldOptionRpc = (request: Init<typeof UpdateTimesheetFieldOptionRequestSchema>): Observable<TimesheetFieldOption> =>
  executorMutation(() => timesheetFieldService.updateTimesheetFieldOption(create(UpdateTimesheetFieldOptionRequestSchema, request)))

export default updateTimesheetFieldOption(updateTimesheetFieldOptionRpc)
